/**
 * RestoID — MFA backup-code binding ("save your backup codes").
 * ---------------------------------------------------------------------------
 * RestoID frame around the PRISTINE upstream logic: the same router-state guard
 * (`backupCodeBindingStateGuard`), the same `UserInteractionContext`
 * verification-id lookup, the same `useTextHandler` copy/download pair (whose
 * `copyText` raises the app toast via the upstream `useToast`), the same
 * native-webview gate on download, and the same `useSendMfaPayload` submit.
 * Replaces Logto's double-title SecondaryPageLayout + secondary-button row with
 * the design's card grid (`BackupBindingScreen` in custom/design-reference/app.js).
 * Swapped in for `./pages/MfaBinding/BackupCodeBinding` via `src/App.tsx`.
 *
 * Deliberate differences from upstream:
 * - No back pill. Upstream renders SecondaryPageLayout with `isNavBarHidden`, so
 *   there is no way back off this screen; adding one would let the user leave
 *   without ever confirming the codes.
 * - Description: the design's own concise line (`mfaSaveCodesDesc`) replaces
 *   `mfa.save_backup_code_description`, and it already says "each code works
 *   once, keep them safe" — which is the whole of upstream's separate
 *   `mfa.backup_code_hint` block, so that block is not rendered a second time.
 *   Same call the other ported MFA screens make (see MfaBackupCodeVerification).
 * - Download/copy are `GhostAction`s rather than upstream's `type="secondary"`
 *   buttons: the RestoID primitives have no outlined button variant, and the
 *   design puts exactly this action under the CTA.
 * - CTA keeps upstream's `action.continue`; the design mock said `action.got_it`,
 *   but this is a step in a flow, not an acknowledgement.
 * - `t` comes from `useTranslation()` instead of upstream's module-level i18next
 *   `t`, so the toast text follows a language switch. Repo convention.
 */
import { MfaFactor } from '@logto/schemas';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { validate } from 'superstruct';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useSendMfaPayload from '@/hooks/use-send-mfa-payload';
import useTextHandler from '@/hooks/use-text-handler';
import { UserMfaFlow } from '@/types';
import { backupCodeBindingStateGuard } from '@/types/guard';
import { isNativeWebview } from '@/utils/native-sdk';

import { Button, C, GhostAction, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

/** Codes are read digit by digit, so they get a real monospace face. */
const MONO = '"SF Mono", ui-monospace, Menlo, monospace';

/**
 * Item type size. A Logto backup code is 12 characters
 * (`generateStandardShortId`); at 15px plus letter-spacing one did not fit the
 * card's half-width column even at the theme's 412px maximum, so it wrapped
 * mid-code. 14px without letter-spacing fits with room to spare, and keeps the
 * `ch` arithmetic below exact (letter-spacing is not part of `ch`).
 */
const CODE_FONT_PX = 14;

const MfaBackupCodeBinding = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const { copyText, downloadText } = useTextHandler();
  const sendMfaPayload = useSendMfaPayload();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { verificationIdsMap } = useContext(UserInteractionContext);
  const verificationId = verificationIdsMap[MfaFactor.BackupCode];

  const { state } = useLocation();
  const [, backupCodeBindingState] = validate(state, backupCodeBindingStateGuard);

  if (!backupCodeBindingState || !verificationId) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { codes } = backupCodeBindingState;
  const backupCodeTextContent = codes.join('\n');

  // Column sizing in `ch` — the monospace advance — so the column is derived from
  // the code rather than the code squeezed into the column. A backup code is a
  // transcription unit: it must never break mid-string, whatever its length.
  const longestCode = codes.reduce((max, code) => Math.max(max, code.length), 0);
  // Width of the widest ordinal, e.g. "10." for ten codes. Fixing it aligns every
  // code on the same left edge.
  const ordinalCh = String(codes.length).length + 1;
  // Code + ordinal + the 0.5ch gutter, plus 1.5ch of slack.
  const columnMin = `${longestCode + ordinalCh + 2}ch`;

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'mfa.save_backup_code' }}>
      <Stack gap={20}>
        <ScreenHead title={t('mfa.save_backup_code')} sub={c('mfaSaveCodesDesc')} />

        {/* The codes themselves — numbered, monospace, two columns where they fit. */}
        <div
          style={{
            display: 'grid',
            // `auto-fit` rather than the design's hard `1fr 1fr`: two columns on a
            // full-width card, one as soon as a whole code no longer fits, so a
            // code is never split across lines. `min(…, 100%)` keeps the track
            // floor from pushing the grid wider than the card on a tiny screen.
            gridTemplateColumns: `repeat(auto-fit, minmax(min(${columnMin}, 100%), 1fr))`,
            gap: 10,
            padding: 18,
            borderRadius: 12,
            background: C.card,
            border: `1px solid ${C.lineSoft}`,
            // Set here, not on the items, so the `ch` units above resolve against
            // the same face the codes are drawn in.
            font: `600 ${CODE_FONT_PX}px/1.6 ${MONO}`,
          }}
        >
          {codes.map((code, index) => (
            <span
              key={code}
              style={{
                // Grid items default to `min-width: auto`, which would let a long
                // code push the card wider than the screen.
                minWidth: 0,
                color: C.text,
                whiteSpace: 'nowrap',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: `${ordinalCh}ch`,
                  marginRight: '0.5ch',
                  color: C.faint,
                  textAlign: 'right',
                }}
              >
                {index + 1}.
              </span>
              {code}
            </span>
          ))}
        </div>

        <Stack gap={12}>
          <Button
            title={t('action.continue')}
            isLoading={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              await sendMfaPayload({
                flow: UserMfaFlow.MfaBinding,
                payload: { type: MfaFactor.BackupCode },
                verificationId,
              });
              setIsSubmitting(false);
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Upstream hides download inside a native webview, where a Blob
                object URL has nowhere to land. Same gate, same order. */}
            {isNativeWebview() ? null : (
              <GhostAction
                icon="download"
                onClick={() => {
                  downloadText(backupCodeTextContent, 'backup_code.txt');
                }}
              >
                {t('action.download')}
              </GhostAction>
            )}
            <GhostAction
              icon="copy"
              onClick={() => {
                void copyText(backupCodeTextContent, t('mfa.backup_code_copied'));
              }}
            >
              {t('action.copy')}
            </GhostAction>
          </div>
        </Stack>
      </Stack>
    </FirstScreenLayout>
  );
};

export default MfaBackupCodeBinding;
